import React, { type ReactNode } from 'react';
import { Icon } from '@noths/polaris-design-system-core-components';

import * as styles from 'src/components/molecules/HorizontalScroller/HorizontalScroller.styles';
import { useHorizontalScroll } from 'src/components/molecules/HorizontalScroller/useHorizontalScroll';

export interface HorizontalScrollerProps {
  ariaLabel?: string;
  children: ReactNode;
  /**
   * Stable identifier of the rendered content. When it changes the strip brings
   * its active item into view (keeping the position when already visible, or
   * resetting to the start when there is no active item), rather than keeping a
   * stale scroll offset.
   */
  scrollResetKey?: unknown;
  testId?: string;
}

export const HorizontalScroller = ({
  ariaLabel,
  children,
  scrollResetKey,
  testId = 'horizontal-scroller',
}: HorizontalScrollerProps) => {
  // Use children as recomputeKey since the scroller content could change
  const { canScrollLeft, canScrollRight, scrollNext, scrollPrev, scrollerRef } =
    useHorizontalScroll<HTMLDivElement>(children, scrollResetKey);

  return (
    <nav aria-label={ariaLabel ?? 'Horizontal scroller'} css={styles.nav} data-testid={testId}>
      {canScrollLeft && (
        <button
          aria-label="Scroll left"
          css={styles.arrow}
          data-testid={`${testId}-prev`}
          onClick={scrollPrev}
          type="button"
        >
          <Icon name="actionBack" size="sm" />
        </button>
      )}
      <div css={styles.viewport}>
        <div css={styles.scrollContainer} ref={scrollerRef}>
          {children}
        </div>
        {canScrollLeft && <span aria-hidden css={styles.fadeLeft} />}
        {canScrollRight && <span aria-hidden css={styles.fadeRight} />}
      </div>
      {canScrollRight && (
        <button
          aria-label="Scroll right"
          css={styles.arrow}
          data-testid={`${testId}-next`}
          onClick={scrollNext}
          type="button"
        >
          <Icon name="actionForward" size="sm" />
        </button>
      )}
    </nav>
  );
};
